import { Link } from 'react-router-dom'
import { APP_NAME, PLAN_LIMITS } from '@/lib/config'

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src="/logo-icon.png" alt={APP_NAME} className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-900">{APP_NAME}</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Sign in
              </Link>
              <Link to="/signup" className="btn-primary">
                Start free trial
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            The Simplest Privacy-First Website Analytics
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            The best free alternative to Google Analytics. Real-time tracking, session replay, GDPR compliant. No personal data collection, no cross-site tracking, under 10KB.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="btn-primary py-3 px-8 text-base">
              Start free - no credit card required
            </Link>
            <a href="#features" className="btn-secondary py-3 px-8 text-base">
              Learn more
            </a>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            Free tier: {PLAN_LIMITS.sessionsPerMonth.toLocaleString()} sessions/month, {PLAN_LIMITS.eventsPerMonth.toLocaleString()} events/month
          </p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Everything you need to understand your traffic
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="card">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Why Choose {APP_NAME}?
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            See how {APP_NAME} compares to other analytics platforms
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {comparisons.map((comp) => (
              <div key={comp.vs} className="card">
                {comp.link ? (
                  <Link to={comp.link} className="text-sm font-medium text-primary-600 mb-2 hover:underline block">
                    vs {comp.vs} →
                  </Link>
                ) : (
                  <p className="text-sm font-medium text-primary-600 mb-2">vs {comp.vs}</p>
                )}
                <ul className="space-y-2">
                  {comp.advantages.map((adv, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-600">
                      <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {adv}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Built for Every Website
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {APP_NAME} auto-detects your platform and works out of the box
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((uc) => (
              <div key={uc.title} className="text-center">
                <div className="text-4xl mb-4">{uc.emoji}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{uc.title}</h3>
                <p className="text-gray-600 text-sm">{uc.description}</p>
                {uc.link && (
                  <Link to={uc.link} className="text-primary-600 text-sm font-medium hover:underline mt-2 inline-block">
                    Learn more →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Start free, scale when ready</h2>
          <p className="text-gray-600 mb-12">Generous free tier. No credit card required.</p>

          <div className="card max-w-md mx-auto">
            <div className="text-center">
              <p className="text-sm font-medium text-primary-600 uppercase mb-2">Free Tier</p>
              <div className="text-4xl font-bold text-gray-900 mb-1">
                Free
              </div>
              <p className="text-sm text-gray-500 mb-6">Get started instantly</p>

              <ul className="space-y-3 mb-8 text-left">
                {[
                  `${PLAN_LIMITS.sessionsPerMonth.toLocaleString()} sessions/month`,
                  `${PLAN_LIMITS.eventsPerMonth.toLocaleString()} events/month`,
                  `${PLAN_LIMITS.dataRetentionDays} days data retention`,
                  'Real-time analytics',
                  'Session replay',
                  'GDPR compliant',
                  'Lightweight tracker (<10KB)',
                  'Unlimited websites',
                ].map((item) => (
                  <li key={item} className="flex items-center text-gray-600">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>

              <Link to="/signup" className="btn-primary w-full py-3">
                Get started for free
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary-600 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to understand your visitors?
          </h2>
          <p className="text-primary-100 mb-8">
            Start tracking in under 2 minutes. No credit card required.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-lg bg-white text-primary-600 hover:bg-primary-50 transition-colors"
          >
            Get started for free
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-200 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/vs-google-analytics" className="text-gray-500 hover:text-gray-700 text-sm">vs Google Analytics</Link>
              <Link to="/for-shopify" className="text-gray-500 hover:text-gray-700 text-sm">Shopify Analytics</Link>
              <Link to="/for-wordpress" className="text-gray-500 hover:text-gray-700 text-sm">WordPress Analytics</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

const features = [
  {
    title: 'Real-time Dashboard',
    description: 'See who is on your site right now with live visitor tracking and instant event updates.',
    icon: ChartIcon,
  },
  {
    title: 'Session Replay',
    description: 'Understand user journeys with event timelines showing every click, scroll, and page view.',
    icon: PlayIcon,
  },
  {
    title: 'Privacy First',
    description: 'First-party cookies only, no personal data collection, fully GDPR compliant. No cross-site tracking.',
    icon: ShieldIcon,
  },
  {
    title: 'Lightweight Script',
    description: 'Our tracker is under 10KB gzipped. Zero impact on your site performance.',
    icon: BoltIcon,
  },
  {
    title: 'Top Pages & Sources',
    description: 'Know your most popular content and where your visitors come from.',
    icon: ChartBarIcon,
  },
  {
    title: 'Easy Integration',
    description: 'Just one script tag. Works with any website, framework, or CMS.',
    icon: CodeIcon,
  },
]

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  )
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  )
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  )
}

function ChartBarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  )
}

function CodeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  )
}

const comparisons = [
  {
    vs: 'Google Analytics',
    link: '/vs-google-analytics',
    advantages: [
      'First-party cookies only (no cross-site tracking)',
      '10x lighter (under 10KB vs 45KB+)',
      'Simple dashboard (no complex setup)',
      'No personal data collection',
    ],
  },
  {
    vs: 'Plausible',
    advantages: [
      'Session replay included',
      'Real-time live visitor counter',
      'Free tier available',
      'Platform auto-detection',
    ],
  },
  {
    vs: 'Fathom',
    advantages: [
      'More affordable pricing',
      'Session replay included',
      'No complex setup required',
      'Free tier available',
    ],
  },
  {
    vs: 'Hotjar',
    advantages: [
      'Privacy-first approach',
      'Much lighter weight',
      'First-party cookies only',
      'Better performance impact',
    ],
  },
]

const useCases = [
  {
    emoji: '🛒',
    title: 'E-commerce',
    description: 'Track Shopify, WooCommerce, and other stores. Auto-detect cart events and checkout flows.',
    link: '/for-shopify',
  },
  {
    emoji: '🚀',
    title: 'SaaS & Startups',
    description: 'Understand user journeys in your React, Next.js, or Vue app. SPA support built-in.',
  },
  {
    emoji: '📝',
    title: 'Blogs & Content',
    description: 'Works with WordPress, Ghost, and any CMS. One-line installation, no plugins needed.',
    link: '/for-wordpress',
  },
]

const faqs = [
  {
    question: 'Is UserInsight.live GDPR compliant?',
    answer: 'Yes. UserInsight.live uses first-party cookies only and does not collect any personal data like IP addresses or user IDs. We do not track users across sites, making us fully GDPR, CCPA, and PECR compliant.',
  },
  {
    question: 'How does UserInsight.live compare to Google Analytics?',
    answer: 'UserInsight.live is simpler, lighter (under 10KB vs 45KB+), and privacy-first. We use first-party cookies only (no cross-site tracking), have a much simpler dashboard, and do not require complex configuration.',
  },
  {
    question: 'How do I install UserInsight.live?',
    answer: 'Just add one script tag to your website. UserInsight.live auto-detects your platform (Shopify, WordPress, React, etc.) and configures itself automatically. No plugins or complex setup required.',
  },
  {
    question: 'Is there a free tier?',
    answer: 'Yes! We offer a free tier with 5,000 sessions/month and 100,000 events/month. No credit card required to start.',
  },
  {
    question: 'Does UserInsight.live slow down my website?',
    answer: 'No. Our tracker is under 10KB gzipped and loads asynchronously. It has zero impact on your page load time and Core Web Vitals.',
  },
]
